import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { getFirebaseAuth } from './firebaseConfig';

const RECAPTCHA_CONTAINER_ID = 'firebase-recaptcha-container-staff';

let recaptchaVerifier = null;
let recaptchaWidgetId = null;

function clearRecaptchaVerifier() {
  const widgetId = recaptchaWidgetId;
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch {
      /* ignore */
    }
  }
  recaptchaVerifier = null;
  recaptchaWidgetId = null;

  if (typeof window !== 'undefined' && window.grecaptcha && widgetId != null) {
    try {
      window.grecaptcha.reset(widgetId);
    } catch {
      /* ignore */
    }
  }

  const existing = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (existing?.parentNode) {
    existing.parentNode.removeChild(existing);
  }
}

function mountRecaptchaContainer() {
  clearRecaptchaVerifier();
  const container = document.createElement('div');
  container.id = RECAPTCHA_CONTAINER_ID;
  container.setAttribute('aria-hidden', 'true');
  container.style.position = 'absolute';
  container.style.width = '1px';
  container.style.height = '1px';
  container.style.overflow = 'hidden';
  container.style.clip = 'rect(0,0,0,0)';
  document.body.appendChild(container);
  return container;
}

async function createRecaptchaVerifier() {
  mountRecaptchaContainer();
  const auth = getFirebaseAuth();
  recaptchaVerifier = new RecaptchaVerifier(auth, RECAPTCHA_CONTAINER_ID, {
    size: 'invisible',
  });
  recaptchaWidgetId = await recaptchaVerifier.render();
  return recaptchaVerifier;
}

export function resetFirebaseRecaptcha() {
  clearRecaptchaVerifier();
}

function toE164IndianPhone(phoneDigits10) {
  const digits = String(phoneDigits10 || '').replace(/\D/g, '').slice(-10);
  if (digits.length !== 10) {
    throw new Error('Enter a valid 10-digit mobile number');
  }
  return `+91${digits}`;
}

export function firebasePhoneErrorMessage(error) {
  const code = error?.code || '';
  if (code === 'auth/invalid-api-key' || String(error?.message || '').toLowerCase().includes('api key not valid')) {
    return 'Login service misconfigured. Ask your manager to rebuild the app — then try again.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }
  if (code === 'auth/invalid-phone-number') {
    return 'Enter a valid 10-digit mobile number';
  }
  if (code === 'auth/code-expired' || code === 'auth/invalid-verification-code') {
    return 'Incorrect or expired code';
  }
  if (code === 'auth/captcha-check-failed') {
    return 'Verification check failed. Please try again.';
  }
  if (code === 'auth/app-not-authorized' || code === 'auth/unauthorized-domain') {
    return 'This login page is not authorized yet. Ask your manager to add manage.thetabletop.in in Firebase, then try again.';
  }
  if (code === 'auth/invalid-app-credential' || code === 'auth/missing-app-credential') {
    return 'SMS login could not start. Reload the page and try again.';
  }
  if (String(error?.message || '').includes('reCAPTCHA has already been rendered')) {
    return 'Please wait a moment and tap Send code again.';
  }
  return error?.message || 'Could not send login code. Please try again.';
}

/** Send Firebase SMS OTP; returns confirmationResult for confirmFirebaseOtp(). */
export async function sendFirebaseOtp(phoneDigits10) {
  const phoneE164 = toE164IndianPhone(phoneDigits10);
  const auth = getFirebaseAuth();
  try {
    const appVerifier = await createRecaptchaVerifier();
    return await signInWithPhoneNumber(auth, phoneE164, appVerifier);
  } catch (error) {
    resetFirebaseRecaptcha();
    throw error;
  }
}

/** Confirm SMS code with Firebase and return a Firebase ID token for backend exchange. */
export async function confirmFirebaseOtp(confirmationResult, code) {
  const cleanCode = String(code || '').replace(/\D/g, '');
  if (cleanCode.length < 6) {
    throw new Error('Enter the 6-digit code from SMS');
  }
  const result = await confirmationResult.confirm(cleanCode);
  resetFirebaseRecaptcha();
  return result.user.getIdToken();
}
